import { useCallback, useEffect, useRef, useState } from 'react';
import { webSocketService } from './useWebSocket';

/**
 * Realtime nudges for a social-channel inbox.
 *
 * Inbound messages arrive at the channel-gateway by webhook, are published to
 * Kafka, relayed by the BFF onto the tenant's socket room, and land here as
 * `inbox:message`. The page refetches on one, so a customer's reply appears in
 * about a second instead of waiting out a poll.
 *
 * THE POLL IS NEVER SLOWED, and that is deliberate. An earlier version slowed
 * it whenever the socket was connected, which was wrong: the BFF's /ws gateway
 * connects perfectly well while its Kafka consumer is disabled (it is optional,
 * and disables itself when no broker is configured). The page then believed it
 * was live, stretched its poll, and received nothing - strictly worse than not
 * having realtime at all. A socket being open says nothing about whether events
 * are flowing through it.
 *
 * So the socket is a pure accelerator here. The poll remains the floor, the
 * cost of which is one small request per interval per open inbox, and the
 * failure mode of a dead relay is "no faster than before" rather than "slower".
 *
 * `isLive` is likewise only true once a message has actually been delivered
 * through the socket, so the badge reports the path that is working rather than
 * the one that is merely connected.
 */
export interface InboxMessageEvent {
  channel: 'whatsapp' | 'facebook';
  conversationId: string;
  participantId: string;
  messageType: string;
  isNewConversation: boolean;
}

export interface InboxRealtimeOptions {
  /** Only fire for this channel; events for others are ignored. */
  channel: 'whatsapp' | 'facebook';
  /** Run when a message for this channel arrives. */
  onMessage: (event: InboxMessageEvent) => void;
}

export interface InboxRealtimeState {
  /**
   * True only while the socket is connected AND it has actually delivered at
   * least one message this session. Connected-but-silent reads as not live.
   */
  isLive: boolean;
}

export function useInboxRealtime({
  channel,
  onMessage,
}: InboxRealtimeOptions): InboxRealtimeState {
  const [connected, setConnected] = useState(() => webSocketService.isConnected());
  const [proven, setProven] = useState(false);

  // Kept in a ref so re-subscribing is not needed every time the caller
  // re-creates its callback, which would tear the listener down mid-message.
  const onMessageRef = useRef(onMessage);
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const handle = useCallback(
    (event: InboxMessageEvent) => {
      if (event?.channel !== channel) return;
      // Proof the whole chain works: gateway -> Kafka -> BFF -> this socket.
      setProven(true);
      onMessageRef.current(event);
    },
    [channel],
  );

  useEffect(() => webSocketService.subscribe('inbox:message', handle), [handle]);

  useEffect(() => {
    // Seed from the live socket as well as subscribing: the connection is
    // opened once at login, so by the time this page mounts the connect event
    // has long since fired and would never be seen.
    setConnected(webSocketService.isConnected());
    return webSocketService.onConnectionChange((next) => {
      setConnected(next);
      // A dropped socket invalidates the proof: whatever comes back may be a
      // different BFF pod, and its relay may not be running.
      if (!next) setProven(false);
    });
  }, []);

  return { isLive: connected && proven };
}
